import { useEffect, useState } from 'react';

import { Col, Row, Skeleton } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  doGetAccountBalance,
  accountsSetValues,
  getTransfers,
  doRemoveRedirectToSummaryAfterAccountSubmit,
  doGetPositionList,
} from 'src/actions';
import { FilterBarConstant, NavigatePathConstant } from 'src/constants';
import { useAccountBalance, usePositions, useFindAccountStatus, useSubmittedAccountSelector } from 'src/hooks';
import { OfferOrderStatusLabel, Transfer, TransferStatusLabel, Position } from 'src/models';
import { Color, Spacing } from 'src/styles';

import { AccountSummary } from './AccountSummary';
import { PortofolioSummaryInfo } from './accountSummary.model';
import { NonTradedHoldingsTable } from './NonTradedHoldingsTable';
import { SecurityHoldingsTable } from './SecurityHoldingsTable';

export const SummaryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [portofolioData, setPortofolioData] = useState<PortofolioSummaryInfo>({});
  const [pendingTransfersTotal, setPendingTransfersTotal] = useState<number | typeof NaN>(0);
  const [nonTradedPositionsTotal, setNonTradedPositionsTotal] = useState<number | typeof NaN>(0);
  const [tradedPositionsTotal, setTradedPositionsTotal] = useState<number | typeof NaN>(0);

  const transfersData: Transfer[] | undefined = useSelector((state: any) => state.cashiering.transfers?.data);
  const isLoadingTransfers = useSelector((state: any) => Boolean(state.cashiering.transfers?.__requested));
  const hasMoneyTransfersFailed = useSelector((state: any) => Boolean(state.cashiering.transfers?.__failed));

  const { account } = useSubmittedAccountSelector();
  const { cashBalance, cashAvailable, pendingOfferOrdersTotal, isAccountBalanceLoading } = useAccountBalance();
  const { tradablePositionList, nonTradablePositionList, isPositionListLoading } = usePositions({
    dependency: [account.accountId],
  });
  useFindAccountStatus({ dependency: [account.accountId] });

  const totalValue = useSelector((state: any) => state.accountDetails?.accountValue?.data?.totalValue);

  const findPendingTransfersTotal = () => {
    const pendingMoneyTransactions = Array.isArray(transfersData)
      ? transfersData?.filter(transfer => transfer.status.isProcessing || transfer.status.isPending)
      : [];

    return hasMoneyTransfersFailed
      ? NaN
      : pendingMoneyTransactions.reduce((acc: number, transfer: Transfer) => {
          if (transfer.direction.isDeposit) return acc + transfer.amount;

          return acc - transfer.amount;
        }, 0);
  };

  const findTradedPositionsTotal = () => {
    return tradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const findNonTradedPositionsTotal = () => {
    return nonTradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const refreshPage = () => {
    dispatch(doGetAccountBalance(account.accountId));
    dispatch(doGetPositionList({ params: { accountId: account.accountId } }));
    dispatch(getTransfers(account.accountId));
  };

  useEffect(() => {
    setPendingTransfersTotal(findPendingTransfersTotal());
  }, [transfersData]);

  useEffect(() => {
    setTradedPositionsTotal(findTradedPositionsTotal());
  }, [tradablePositionList]);

  useEffect(() => {
    setNonTradedPositionsTotal(findNonTradedPositionsTotal());
  }, [nonTradablePositionList]);

  useEffect(() => {
    const accountValue = cashBalance + tradedPositionsTotal + nonTradedPositionsTotal;

    dispatch(
      accountsSetValues({
        totalValue: accountValue,
        pendingCashTransactions: pendingTransfersTotal,
        cashAvailable,
      }),
    );
  }, [
    account.accountId,
    cashBalance,
    tradedPositionsTotal,
    nonTradedPositionsTotal,
    pendingTransfersTotal,
    cashAvailable,
  ]);

  useEffect(() => {
    setPortofolioData({
      cashAvailable: {
        title: 'Cash Available',
        color: Color.GREEN.GREEN7,
        totalValue: cashAvailable,
        isExpandible: false,
        alwaysShow: true,
      },
      pendingTransations: {
        title: 'Pending Money Transactions',
        color: Color.ORANGE.ORANGE7,
        totalValue: pendingTransfersTotal,
        isExpandible: true,
        onClick: () =>
          navigate(
            `../${NavigatePathConstant.HISTORY}?show=${JSON.stringify([
              FilterBarConstant.ShowFilterCategory.DepositTransfer,
              FilterBarConstant.ShowFilterCategory.WithdrawalTransfer,
            ])}&filterBy=${JSON.stringify([
              `${TransferStatusLabel.PENDING}_${FilterBarConstant.ByFilterCategory.MoneyTransfer}`,
              `${TransferStatusLabel.PROCESSING}_${FilterBarConstant.ByFilterCategory.MoneyTransfer}`,
            ])}`,
          ),
      },
      pendingNonTradedHoldings: {
        title: 'Pending Investment(s)',
        color: Color.RED.RED7,
        totalValue: pendingOfferOrdersTotal,
        isExpandible: true,
        onClick: () =>
          navigate(
            `../${NavigatePathConstant.HISTORY}?show=${JSON.stringify([
              FilterBarConstant.ShowFilterCategory.OfferOrder,
            ])}&filterBy=${JSON.stringify([
              `${OfferOrderStatusLabel.PendingFunds}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.PendingOfferClose}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.PendingAction}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.PendingFirmCancellation}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
              `${OfferOrderStatusLabel.Approved}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
            ])}`,
          ),
      },
      securities: {
        title: 'Securities Holdings',
        color: Color.BRAND.BRAND7,
        isExpandible: false,
        totalValue: tradedPositionsTotal,
      },
      nonTradedHoldings: {
        title: 'Non-Traded Holdings',
        color: Color.BRAND.BRAND9,
        totalValue: nonTradedPositionsTotal,
        isExpandible: false,
      },
    });
  }, [cashAvailable, pendingTransfersTotal, pendingOfferOrdersTotal, tradedPositionsTotal, nonTradedPositionsTotal]);

  useEffect(() => {
    // TODO: introduce useTransfers custom hook and trigger when the account.accountId changes
    dispatch(getTransfers(account.accountId));
  }, [account.accountId]);

  useEffect(() => {
    dispatch(doRemoveRedirectToSummaryAfterAccountSubmit());
  }, [dispatch]);

  return (
    <Skeleton loading={isPositionListLoading || isLoadingTransfers || isAccountBalanceLoading}>
      <Row>
        <Col span={24}>
          <AccountSummary
            title='Summary'
            isLoading={isPositionListLoading || isLoadingTransfers || isAccountBalanceLoading}
            accountValue={totalValue}
            infoTitle='Account Details'
            portofolio={portofolioData}
            onRefresh={refreshPage}
          />
        </Col>
        <Col span={24} id='securities-section' className={Spacing.my24}>
          <SecurityHoldingsTable />
        </Col>
        <Col span={24} id='non-trade-holdings-section' className={Spacing.my24}>
          <NonTradedHoldingsTable />
        </Col>
      </Row>
    </Skeleton>
  );
};
